import { useEffect, useState } from 'react';
import { withValueCap } from 'pages/globals/global';
import { NumericFormat } from 'react-number-format';
import { processArrays } from '../LeaseUtils/helper';
import AddIconButton from 'components/button/AddIconButton';
import DeleteIconButton from 'components/button/DeleteIconButton';
import { App, Button, Form, Input, Modal, Tooltip } from 'antd';
import GrossRentHistory from '../LeaseUtils/History/GrossRentHistory';
import { Table, Stack, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

export const transformRangeDataToAmountArray = (rangeData) => {
	const result = [];

	rangeData.forEach((range) => {
		const fromValue = parseInt(range.from);
		const toValue = parseInt(range.to);

		if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
			console.warn('Invalid range found:', range);
			return;
		}

		const amount = parseFloat(String(range.amount).replace(/,/g, ''));

		if (isNaN(amount)) {
			console.warn('Invalid amount found:', range);
			return;
		}

		const repetitions = toValue - fromValue + 1;

		for (let i = 0; i < repetitions; i++) {
			result.push(amount);
		}
	});

	return result;
};

const GrossRent = ({ screen = 'CREATE' }) => {
	const form = Form.useFormInstance();
	const { message: messageApi } = App.useApp();

	const grossRentListSet = form.getFieldValue('grossRentListSet');
	const [openModal, setOpenModal] = useState(false);
	const grossRentListSetForHistory = grossRentListSet?.length > 1 ? [...grossRentListSet]?.slice(0, -1) : [];

	const callDebounced = () => {
		if (screen === 'EDIT' && window.debouncedManageChangeRows) {
			window.debouncedManageChangeRows();
		}
	};

	const handleAmountChange = (fieldName) => {
		const timeoutId = setTimeout(() => {
			handleApply();
		}, 10);

		return () => {
			clearTimeout(timeoutId);
		};
	};

	const handleToChange = (fieldName) => {
		setTimeout(handleApply, 10);
	};

	const validateToValueOnBlur = (index) => {
		const currentValues = form.getFieldValue('grossRentList');
		if (!currentValues) return;

		const toValue = parseInt(currentValues[index].to);
		const fromValue = parseInt(currentValues[index].from);

		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
			messageApi.error("'To' value must be greater than or equal to 'From' value");
			form.setFields([
				{
					name: ['grossRentList', index, 'to'],
					value: '',
					errors: ["'To' value must be greater than 'From' value"],
				},
			]);
		}

		setTimeout(handleApply, 10);
	};

	const isRowValid = (row) => {
		if (!row) return false;

		const toValue = parseInt(row.to);
		const fromValue = parseInt(row.from);

		if (isNaN(toValue) || toValue <= 0) return false;
		if (isNaN(fromValue) || fromValue <= 0) return false;
		if (toValue < fromValue) return false;
		if (!row.amount) return false;

		return true;
	};

	const handleAddRow = (index) => {
		const leaseType = form.getFieldValue('leaseType');
		const currentValues = form.getFieldValue('grossRentList');

		if (!currentValues || !currentValues.length) {
			form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
			return;
		}

		const currentRow = currentValues[index];

		if (!isRowValid(currentRow)) {
			messageApi.error('Please complete all required fields in the current row before adding a new one');
			return;
		}

		const toValue = parseInt(currentRow.to);

		if (leaseType === 'Operating Lease' && toValue >= 12) {
			messageApi.error('Maximum periods taken');
			return;
		}
		const newFrom = toValue + 1;

		const newRow = { from: newFrom, to: '', amount: '', remarks: '' };

		const updatedValues = [...currentValues.slice(0, index + 1), newRow, ...currentValues.slice(index + 1)];

		for (let i = index + 2; i < updatedValues.length; i++) {
			const prevTo = parseInt(updatedValues[i - 1].to);
			if (!isNaN(prevTo)) {
				updatedValues[i].from = prevTo + 1;
			}
		}

		form.setFieldValue('grossRentList', updatedValues);

		setTimeout(handleApply, 10);
	};

	const handleApply = () => {
		const currentValues = form.getFieldValue('grossRentList');

		if (!currentValues || currentValues.length === 0) return;
		const transformedData = transformRangeDataToAmountArray(currentValues);

		form.setFieldValue('grossRents', transformedData);
		const currentAdvanceAmountsValues = form.getFieldValue('advanceAmountList');
		if (!currentAdvanceAmountsValues || currentAdvanceAmountsValues?.length === 0) return;

		const transformedDataAdvanceAmounts = transformRangeDataToAmountArray(currentAdvanceAmountsValues);

		const { processedAdvanceAmounts } = processArrays(transformedData, transformedDataAdvanceAmounts);

		form.setFieldValue('advanceAmounts', processedAdvanceAmounts);
		callDebounced();
	};

	useEffect(() => {
		const currentValues = form.getFieldValue('grossRentList');
		if (!currentValues || !currentValues.length) {
			form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
		}

		handleApply();
	}, [form]);

	return (
		<>
			<Form.List name={'grossRentList'}>
				{(fields) => (
					<>
						<TableContainer
							className="gross-rent-table"
							sx={{
								'& table, & th, & td': {
									border: '1px solid #e0e0e0',
									borderCollapse: 'collapse',
								},
								'& th, & td': {
									p: '5px',
								},
								'& td .ant-form-item': {
									marginBottom: '0px',
								},

								'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' },
								'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' },
								'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' },
								'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' },
								'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' },
								'& table': { width: '100%' },
							}}
						>
							<Table aria-label="Gross Rent table">
								<TableHead>
									<TableRow>
										<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>From Month</TableCell>
										<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>To Month</TableCell>
										<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>Amount</TableCell>
										{screen !== 'VIEW' && (
											<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>Action</TableCell>
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{fields.length > 0 ? (
										fields.map((field, index) => (
											<TableRow key={field.key}>
												<TableCell>
													<Form.Item name={[field.name, 'from']}>
														<Input
															disabled={true}
															style={{ width: '100%', backgroundColor: '#f5f5f5', textAlign: 'center' }}
														/>
													</Form.Item>
												</TableCell>

												<TableCell>
													<Form.Item
														name={[field.name, 'to']}
														rules={[
															{ required: true, message: "Please enter a 'To' value" },
															({ getFieldValue }) => ({
																validator(_, value) {
																	const leaseType = getFieldValue('leaseType');
																	const fromValue = getFieldValue(['grossRentList', field.name, 'from']);
																	if (!value || !fromValue) return Promise.resolve();
																	if (parseInt(value) < parseInt(fromValue)) {
																		return Promise.reject(new Error("'To' must be greater than 'From'"));
																	}
																	if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
																		return Promise.reject(new Error('Maximum month is 12'));
																	}
																	return Promise.resolve();
																},
															}),
														]}
													>
														<Input
															type="number"
															disabled={screen === 'VIEW'}
															placeholder="Enter To value"
															style={{
																width: '100%',
																textAlign: 'center',
															}}
															onChange={() => handleToChange(`${'grossRentList'}.${field.name}.to`)}
															onBlur={() => validateToValueOnBlur(index)}
														/>
													</Form.Item>
												</TableCell>
												<TableCell>
													<Form.Item
														name={[field.name, 'amount']}
														rules={[{ required: true, message: 'Please enter an amount' }]}
													>
														<NumericFormat
															decimalScale={2}
															placeholder="0.00"
															fixedDecimalScale
															thousandSeparator=","
															isAllowed={withValueCap}
															customInput={Input}
															disabled={screen === 'VIEW'}
															onValueChange={(values) => {
																handleAmountChange(`${'grossRentList'}.${field.name}.amount`);
																const currentValues = [...form.getFieldValue('grossRentList')];

																if (currentValues[index]) {
																	currentValues[index].amount = values.value || '';

																	form.setFieldValue('grossRentList', currentValues);
																}
															}}
															onBlur={() => setTimeout(handleApply, 10)}
														/>
													</Form.Item>
												</TableCell>

												{screen !== 'VIEW' && (
													<TableCell align="center">
														<Stack sx={{ flexDirection: 'row', justifyContent: 'center' }}>
															{fields.length === 1 && index === 0 && (
																<Tooltip title="Add Row">
																	<AddIconButton onClick={() => handleAddRow(index)} />
																</Tooltip>
															)}

															{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																<Tooltip title="Delete Row">
																	<DeleteIconButton
																		onClick={() => {
																			const currentValues = [...form.getFieldValue('grossRentList')];

																			currentValues.splice(index, 1);

																			for (let i = index; i < currentValues.length; i++) {
																				if (i > 0) {
																					const prevTo = parseInt(currentValues[i - 1].to);
																					if (!isNaN(prevTo)) {
																						currentValues[i].from = prevTo + 1;
																					}
																				}
																			}

																			form.setFieldValue('grossRentList', currentValues);

																			setTimeout(handleApply, 10);
																		}}
																	/>
																</Tooltip>
															)}

															{fields.length > 1 && index === fields.length - 1 && (
																<>
																	<Tooltip title="Add Row">
																		<AddIconButton onClick={() => handleAddRow(index)} />
																	</Tooltip>

																	<Tooltip title="Delete Row">
																		<DeleteIconButton
																			onClick={() => {
																				const currentValues = [...form.getFieldValue('grossRentList')];

																				currentValues.splice(index, 1);

																				for (let i = index; i < currentValues.length; i++) {
																					if (i > 0) {
																						const prevTo = parseInt(currentValues[i - 1].to);
																						if (!isNaN(prevTo)) {
																							currentValues[i].from = prevTo + 1;
																						}
																					}
																				}

																				form.setFieldValue('grossRentList', currentValues);

																				setTimeout(handleApply, 10);
																			}}
																		/>
																	</Tooltip>
																</>
															)}

															{fields.length > 1 && index === 0 && <span></span>}
														</Stack>
													</TableCell>
												)}
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell colSpan={5} style={{ textAlign: 'center' }}>
												No data. Click plus icon in action column to add new entries.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>

						<Form.Item name="grossRents" noStyle />
					</>
				)}
			</Form.List>

			<Modal
				title={'Gross Rent Modifications'}
				open={openModal}
				onCancel={() => setOpenModal(false)}
				width={800}
				footer={[
					<Button key="cancel" onClick={() => setOpenModal(false)}>
						Close
					</Button>,
				]}
			>
				{grossRentListSetForHistory?.map((item, index) => (
					<GrossRentHistory key={index} data={item} index={index} />
				))}
			</Modal>
		</>
	);
};

export default GrossRent;

// import { useEffect, useState } from 'react';
// import { withValueCap } from 'pages/globals/global';
// import { NumericFormat } from 'react-number-format';
// import { processArrays } from '../LeaseUtils/helper';
// import { Button, Form, Input, message, Modal, Tooltip } from 'antd';
// import GrossRentHistory from '../LeaseUtils/History/GrossRentHistory';
// import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
// import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

// export const transformRangeDataToAmountArray = (rangeData) => {
// 	// Initialize an empty result array
// 	const result = [];

// 	// Process each range object
// 	rangeData.forEach((range) => {
// 		const fromValue = parseInt(range.from);
// 		const toValue = parseInt(range.to);

// 		// Skip invalid ranges
// 		if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
// 			console.warn('Invalid range found:', range);
// 			return;
// 		}

// 		// Convert amount string to number (remove commas)
// 		const amount = parseFloat(String(range.amount).replace(/,/g, ''));

// 		// Skip if amount is not a valid number
// 		if (isNaN(amount)) {
// 			console.warn('Invalid amount found:', range);
// 			return;
// 		}

// 		// Calculate how many times to repeat the amount ((to - from) + 1)
// 		const repetitions = toValue - fromValue + 1;

// 		// Add the amount to the result array the specified number of times
// 		for (let i = 0; i < repetitions; i++) {
// 			result.push(amount);
// 		}
// 	});

// 	return result;
// };
// const GrossRent = ({ screen = 'CREATE' }) => {
// 	const form = Form.useFormInstance();
// 	const grossRentListSet = form.getFieldValue('grossRentListSet');
// 	const [openModal, setOpenModal] = useState(false);
// 	const grossRentListSetForHistory = grossRentListSet?.length > 1 ? [...grossRentListSet]?.slice(0, -1) : [];

// 	// State to track which values have been modified (for highlighting)
// 	// const [modifiedFields, setModifiedFields] = useState({});

// 	// Call debouncedManageChangeRows when the component's values change
// 	const callDebounced = () => {
// 		if (screen === 'EDIT' && window.debouncedManageChangeRows) {
// 			window.debouncedManageChangeRows();
// 		}
// 	};

// 	// Handler for when an amount changes
// 	const handleAmountChange = (fieldName) => {
// 		// Mark this field as modified for highlighting
// 		// setModifiedFields((prev) => ({
// 		// 	...prev,
// 		// 	[fieldName]: true,
// 		// }));

// 		// Automatically apply the changes
// 		const timeoutId = setTimeout(() => {
// 			handleApply();
// 		}, 10);

// 		return () => {
// 			clearTimeout(timeoutId);
// 		};
// 	};

// 	// Handler for "To" value changes
// 	const handleToChange = (fieldName) => {
// 		// Only mark this field as modified for highlighting
// 		// setModifiedFields((prev) => ({
// 		// 	...prev,
// 		// 	[fieldName]: true,
// 		// }));

// 		// Automatically apply the changes
// 		setTimeout(handleApply, 10);
// 	};

// 	// Validate To value ONLY on blur to allow typing multi-digit numbers
// 	const validateToValueOnBlur = (index) => {
// 		// Get the current values
// 		const currentValues = form.getFieldValue('grossRentList');
// 		if (!currentValues) return;

// 		const toValue = parseInt(currentValues[index].to);
// 		const fromValue = parseInt(currentValues[index].from);

// 		// Validate that To is greater than or equal to From
// 		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
// 			message.error("'To' value must be greater than or equal to 'From' value");
// 			form.setFields([
// 				{
// 					name: ['grossRentList', index, 'to'],
// 					value: '',
// 					errors: ["'To' value must be greater than 'From' value"],
// 				},
// 			]);
// 		}

// 		// Automatically apply the changes
// 		setTimeout(handleApply, 10);
// 	};

// 	// Validate if a row is complete and valid
// 	const isRowValid = (row) => {
// 		if (!row) return false;

// 		// Check if all required fields are filled
// 		const toValue = parseInt(row.to);
// 		const fromValue = parseInt(row.from);

// 		if (isNaN(toValue) || toValue <= 0) return false;
// 		if (isNaN(fromValue) || fromValue <= 0) return false;
// 		if (toValue < fromValue) return false;
// 		if (!row.amount) return false;

// 		return true;
// 	};

// 	// Handle adding a new row - this now adds a row after the current one
// 	const handleAddRow = (index) => {
// 		const leaseType = form.getFieldValue('leaseType');
// 		const currentValues = form.getFieldValue('grossRentList');

// 		// Initialize with first row if no values
// 		if (!currentValues || !currentValues.length) {
// 			form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
// 			return;
// 		}

// 		// Get the row to validate
// 		const currentRow = currentValues[index];

// 		// Check if the current row is complete
// 		if (!isRowValid(currentRow)) {
// 			message.error('Please complete all required fields in the current row before adding a new one');
// 			return;
// 		}

// 		// Get the "to" value from the current row
// 		const toValue = parseInt(currentRow.to);

// 		if (leaseType === 'Operating Lease' && toValue >= 12) {
// 			message.error('Maximum periods taken');
// 			return;
// 		}
// 		// New row's "From" value is current row's "To" value + 1
// 		const newFrom = toValue + 1;

// 		// Create the new row
// 		const newRow = { from: newFrom, to: '', amount: '', remarks: '' };

// 		// Insert the new row after the current one
// 		const updatedValues = [...currentValues.slice(0, index + 1), newRow, ...currentValues.slice(index + 1)];

// 		// Fix the From values for all subsequent rows
// 		for (let i = index + 2; i < updatedValues.length; i++) {
// 			const prevTo = parseInt(updatedValues[i - 1].to);
// 			if (!isNaN(prevTo)) {
// 				updatedValues[i].from = prevTo + 1;
// 			}
// 		}

// 		form.setFieldValue('grossRentList', updatedValues);

// 		// Automatically apply the changes
// 		setTimeout(handleApply, 10);
// 	};

// 	const handleApply = () => {
// 		const currentValues = form.getFieldValue('grossRentList');

// 		if (!currentValues || currentValues.length === 0) return;
// 		const transformedData = transformRangeDataToAmountArray(currentValues);

// 		form.setFieldValue('grossRents', transformedData);
// 		const currentAdvanceAmountsValues = form.getFieldValue('advanceAmountList');
// 		if (!currentAdvanceAmountsValues || currentAdvanceAmountsValues?.length === 0) return;

// 		const transformedDataAdvanceAmounts = transformRangeDataToAmountArray(currentAdvanceAmountsValues);

// 		const { processedAdvanceAmounts } = processArrays(transformedData, transformedDataAdvanceAmounts);

// 		// First set the advanceAmounts array for API submission
// 		form.setFieldValue('advanceAmounts', processedAdvanceAmounts);
// 		// Call debouncedManageChangeRows when data changes in Edit mode
// 		callDebounced();
// 	};

// 	// Initialize with the first row
// 	useEffect(() => {
// 		const currentValues = form.getFieldValue('grossRentList');
// 		if (!currentValues || !currentValues.length) {
// 			// Initialize with one row, from=1
// 			form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
// 		}

// 		// Automatically apply initial data
// 		handleApply();
// 	}, [form]);

// 	// const handleHistory = () => {
// 	// 	setOpenModal(true);
// 	// };

// 	return (
// 		<>
// 			<Form.List name={'grossRentList'}>
// 				{(fields) => (
// 					<>
// 						{/* <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
// 							{grossRentListSetForHistory?.length !== 0 && (screen?.toLowerCase() === 'view' || screen?.toLowerCase() === 'edit') && (
// 								<Col>
// 									<Button type="primary" onClick={handleHistory}>
// 										History
// 									</Button>
// 								</Col>
// 							)}
// 						</Row> */}

// 						<TableContainer
// 							className="gross-rent-table"
// 							sx={{
// 								'& table, & th, & td': {
// 									border: '1px solid #e0e0e0',
// 									borderCollapse: 'collapse',
// 								},
// 								'& th, & td': {
// 									p: '5px',
// 								},
// 								'& td .ant-form-item': {
// 									marginBottom: '0px',
// 								},

// 								'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' } /* From/From Month */,
// 								'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' } /* To/To Month */,
// 								'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' } /* Amount/Monthly Adjustment */,
// 								'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' } /* Remarks/Total Amount */,
// 								'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' } /* Action */,
// 								'& table': { width: '100%' } /* Ensure table takes full container width */,
// 							}}
// 						>
// 							<Table aria-label="Gross Rent table">
// 								<TableHead>
// 									<TableRow>
// 										<TableCell sx={{ textAlign: 'center' }}>From Month</TableCell>
// 										<TableCell sx={{ textAlign: 'center' }}>To Month</TableCell>
// 										<TableCell sx={{ textAlign: 'center' }}>Amount</TableCell>
// 										{/* <TableCell sx={{ textAlign: 'center' }}>Notes</TableCell> */}
// 										{screen !== 'VIEW' && <TableCell sx={{ textAlign: 'center' }}>Action</TableCell>}
// 									</TableRow>
// 								</TableHead>
// 								<TableBody>
// 									{fields.length > 0 ? (
// 										fields.map((field, index) => (
// 											<TableRow key={field.key}>
// 												<TableCell>
// 													<Form.Item name={[field.name, 'from']}>
// 														<Input
// 															disabled={true} // Always disabled to prevent manual editing
// style={{// 																padding: '8px',
// 																width: '100%',
// 																backgroundColor: '#f5f5f5', // Grey background to indicate disabled
// 																textAlign: 'center',
//}}
// 														/>
// 													</Form.Item>
// 												</TableCell>

// 												<TableCell>
// 													<Form.Item
// 														name={[field.name, 'to']}
// 														rules={[
// 															{ required: true, message: "Please enter a 'To' value" },
// 															({ getFieldValue }) => ({
// 																validator(_, value) {
// 																	const leaseType = getFieldValue('leaseType');
// 																	const fromValue = getFieldValue(['grossRentList', field.name, 'from']);
// 																	if (!value || !fromValue) return Promise.resolve();
// 																	if (parseInt(value) < parseInt(fromValue)) {
// 																		return Promise.reject(new Error("'To' must be greater than 'From'"));
// 																	}
// 																	if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
// 																		return Promise.reject(new Error('Maximum month is 12'));
// 																	}
// 																	return Promise.resolve();
// 																},
// 															}),
// 														]}
// 													>
// 														<Input
// 															type="number"
// 															disabled={screen === 'VIEW'}
// 															placeholder="Enter To value"
// style={{// 																width: '100%',
// 																padding: '8px',

// 																textAlign: 'center',
//}}
// 															onChange={() => handleToChange(`${'grossRentList'}.${field.name}.to`)}
// 															onBlur={() => validateToValueOnBlur(index)}
// 														/>
// 													</Form.Item>
// 												</TableCell>
// 												<TableCell>
// 													<Form.Item
// 														name={[field.name, 'amount']}
// 														rules={[{ required: true, message: 'Please enter an amount' }]}
// 													>
// 														<NumericFormat
//
// 															decimalScale={2}
// 															placeholder="0.00"
// 															fixedDecimalScale
// 															thousandSeparator=","
// 															isAllowed={withValueCap}
// style={{// 																padding: '18px',
//
//
//
//

// 																// backgroundColor: modifiedFields[`${'grossRentList'}.${field.name}.amount`]
// 																// 	? '#f0f7ff'
// 																// 	: undefined,
//}}
// 															customInput={Input}
// 															disabled={screen === 'VIEW'}
// 															onValueChange={(values) => {
// 																handleAmountChange(`${'grossRentList'}.${field.name}.amount`);
// 																// Get the current form values
// 																const currentValues = [...form.getFieldValue('grossRentList')];

// 																// Update the current field with the new value (could be empty)
// 																if (currentValues[index]) {
// 																	// Update with the numeric value (not formatted), or empty string if deleted
// 																	currentValues[index].amount = values.value || '';

// 																	// Update the form with the new value
// 																	form.setFieldValue('grossRentList', currentValues);
// 																}
// 															}}
// 															// Add onBlur handler to ensure value is applied
// 															onBlur={() => setTimeout(handleApply, 10)}
// 														/>
// 													</Form.Item>
// 												</TableCell>

// 												{/* <TableCell>
// 													<Form.Item name={[field.name, 'remarks']}>
// 														<Input
// 															placeholder="Enter remarks"
// 															disabled={screen === 'VIEW'}
// style={{// 																padding: '8px',
//}}
// 															onChange={() => {
// 																// setModifiedFields((prev) => ({
// 																// 	...prev,
// 																// 	[`${'grossRentList'}.${field.name}.remarks`]: true,
// 																// }));

// 																// Automatically apply the changes
// 																setTimeout(handleApply, 10);
// 															}}
// 														/>
// 													</Form.Item>
// 												</TableCell> */}

// 												{screen !== 'VIEW' && (
// 													<TableCell align="center">
// 														<>
// 															{/* Logic for displaying icons based on row position */}
// 															{/* First row with only one row in total - only plus icon */}
// 															{fields.length === 1 && index === 0 && (
// 																<Tooltip title="Add Row">
// 																	<PlusCircleOutlined
// 																		style={{
// 																			fontSize: '20px',
// 																			color: '#1890ff',
// 																			cursor: 'pointer',
// 																		}}
// 																		onClick={() => handleAddRow(index)}
// 																	/>
// 																</Tooltip>
// 															)}

// 															{/* Middle rows - only minus icon */}
// 															{fields.length > 1 && index > 0 && index < fields.length - 1 && (
// 																<Tooltip title="Delete Row">
// 																	<MinusCircleOutlined
// 																		style={{
// 																			fontSize: '20px',
// 																			color: '#ff4d4f',
// 																			cursor: 'pointer',
// 																		}}
// 																		onClick={() => {
// 																			const currentValues = [...form.getFieldValue('grossRentList')];

// 																			// Remove the row
// 																			currentValues.splice(index, 1);

// 																			// Fix the From/To sequence if needed
// 																			for (let i = index; i < currentValues.length; i++) {
// 																				// If not the first row, From should be previous row's To + 1
// 																				if (i > 0) {
// 																					const prevTo = parseInt(currentValues[i - 1].to);
// 																					if (!isNaN(prevTo)) {
// 																						currentValues[i].from = prevTo + 1;
// 																					}
// 																				}
// 																			}

// 																			form.setFieldValue('grossRentList', currentValues);

// 																			// Automatically apply the changes
// 																			setTimeout(handleApply, 10);
// 																		}}
// 																	/>
// 																</Tooltip>
// 															)}

// 															{/* Last row (but not first) - both plus and minus icons */}
// 															{fields.length > 1 && index === fields.length - 1 && (
// 																<>
// 																	<Tooltip title="Add Row">
// 																		<PlusCircleOutlined
// 																			style={{
// 																				fontSize: '20px',
// 																				color: '#1890ff',
// 																				marginRight: '12px',
// 																				cursor: 'pointer',
// 																			}}
// 																			onClick={() => handleAddRow(index)}
// 																		/>
// 																	</Tooltip>
// 																	<Tooltip title="Delete Row">
// 																		<MinusCircleOutlined
// 																			style={{
// 																				fontSize: '20px',
// 																				color: '#ff4d4f',
// 																				cursor: 'pointer',
// 																			}}
// 																			onClick={() => {
// 																				const currentValues = [...form.getFieldValue('grossRentList')];

// 																				// Remove the row
// 																				currentValues.splice(index, 1);

// 																				// Fix the From/To sequence if needed
// 																				for (let i = index; i < currentValues.length; i++) {
// 																					// If not the first row, From should be previous row's To + 1
// 																					if (i > 0) {
// 																						const prevTo = parseInt(currentValues[i - 1].to);
// 																						if (!isNaN(prevTo)) {
// 																							currentValues[i].from = prevTo + 1;
// 																						}
// 																					}
// 																				}

// 																				form.setFieldValue('grossRentList', currentValues);

// 																				// Automatically apply the changes
// 																				setTimeout(handleApply, 10);
// 																			}}
// 																		/>
// 																	</Tooltip>
// 																</>
// 															)}

// 															{/* First row with multiple rows - no icons */}
// 															{fields.length > 1 && index === 0 && <span></span>}
// 														</>
// 													</TableCell>
// 												)}
// 											</TableRow>
// 										))
// 									) : (
// 										<TableRow>
// 											<TableCell colSpan={5} style={{ textAlign: 'center' }}>
// 												No data. Click plus icon in action column to add new entries.
// 											</TableCell>
// 										</TableRow>
// 									)}
// 								</TableBody>
// 							</Table>
// 						</TableContainer>

// 						{/* Hidden form field to store processed data for API submission */}
// 						<Form.Item name="grossRents" noStyle />
// 					</>
// 				)}
// 			</Form.List>

// 			<Modal
// 				title={'Gross Rent Modifications'}
// 				open={openModal}
// 				onCancel={() => setOpenModal(false)}
// 				width={800}
// 				footer={[
// 					<Button key="cancel" onClick={() => setOpenModal(false)}>
// 						Close
// 					</Button>,
// 				]}
// 			>
// 				{grossRentListSetForHistory?.map((item, index) => (
// 					<GrossRentHistory key={index} data={item} index={index} />
// 				))}
// 			</Modal>
// 		</>
// 	);
// };

// export default GrossRent;
